/**
 * SpecificationViewer Component
 * Displays generated specifications with all sections and version history
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  Description as SpecIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { GeneratedSpecification } from '../../types/estimation';
import { EstimationService } from '../../services/EstimationService';

interface SpecificationViewerProps {
  specification: GeneratedSpecification;
  allVersions?: GeneratedSpecification[];
  estimationId: number;
}

const SPEC_SECTIONS = [
  { key: 'product_definition', label: 'Product Definition', icon: '📋' },
  { key: 'data_model', label: 'Data Model', icon: '🗄️' },
  { key: 'task_breakdown', label: 'Task Breakdown', icon: '✅' },
  { key: 'api_specification', label: 'API Specification', icon: '🔌' },
  { key: 'ui_wireframes', label: 'UI Wireframes', icon: '🎨' },
  { key: 'full_specification', label: 'Full Specification', icon: '📄' },
];

export const SpecificationViewer: React.FC<SpecificationViewerProps> = ({
  specification,
  allVersions = [],
  estimationId,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVersion, setSelectedVersion] = useState(specification);

  const handleDownload = () => {
    alert('PDF download will be implemented in backend');
  };

  const renderSection = (sectionKey: string) => {
    const content = (selectedVersion as any)[sectionKey];
    
    if (!content) {
      return (
        <Alert severity="info">
          This section is not yet available. It will be generated during specification processing.
        </Alert>
      );
    }

    return (
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body1"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
          }}
        >
          {content}
        </Typography>
      </Box>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              <SpecIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Generated Specification
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Version ${selectedVersion.version}`}
                color="primary"
                size="small"
              />
              <Chip
                label={selectedVersion.ai_provider}
                size="small"
                variant="outlined"
              />
              {selectedVersion.estimated_hours && (
                <Chip
                  label={`${selectedVersion.estimated_hours}h estimated`}
                  size="small"
                />
              )}
              {selectedVersion.estimated_cost_usd && (
                <Chip
                  label={EstimationService.formatCost(
                    selectedVersion.estimated_cost_usd
                  )}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {SPEC_SECTIONS.map((section, index) => (
            <Tab
              key={section.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{section.icon}</span>
                  <span>{section.label}</span>
                </Box>
              }
            />
          ))}
          {allVersions.length > 1 && (
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon />
                  <span>Version History</span>
                </Box>
              }
            />
          )}
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {activeTab < SPEC_SECTIONS.length ? (
            renderSection(SPEC_SECTIONS[activeTab].key)
          ) : (
            <Stack spacing={2}>
              <Typography variant="h6">Version History</Typography>
              {allVersions
                .sort((a, b) => b.version - a.version)
                .map((version) => (
                  <Card
                    key={version.id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      bgcolor:
                        version.id === selectedVersion.id
                          ? 'action.selected'
                          : undefined,
                    }}
                    onClick={() => {
                      setSelectedVersion(version);
                      setActiveTab(0);
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1">
                            Version {version.version}
                            {version.is_latest && (
                              <Chip
                                label="Latest"
                                color="primary"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Generated on{' '}
                            {EstimationService.formatDate(version.created_at)} •{' '}
                            {version.ai_provider} • {version.estimated_hours}h
                          </Typography>
                        </Box>
                        {version.completion_percentage !== undefined && (
                          <Chip
                            label={`${version.completion_percentage}% complete`}
                            color={
                              version.completion_percentage === 100
                                ? 'success'
                                : 'warning'
                            }
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SpecificationViewer;
